import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LogoMark } from "./Logo";

const LINKS = [
  ["Services", "#services"],
  ["Why Us", "#why-us"],
  ["About", "#about"],
  ["Pricing", "#pricing"],
  ["FAQ", "#faq"],
  ["Contact", "#contact"],
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const lenis = useLenis();
  const navigate = useNavigate();
  const location = useLocation();

  const go = (hash) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + hash);
      return;
    }
    lenis?.scrollTo(hash, { offset: -64, duration: 1.4 });
  };

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/10" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <a href="#top" onClick={go("#top")} data-testid="nav-logo" className="flex items-center">
            <LogoMark className="h-7 w-auto" />
          </a>
          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map(([label, hash]) => (
              <a
                key={hash}
                href={hash}
                onClick={go(hash)}
                data-testid={`nav-link-${label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400 hover:text-white transition-colors"
              >
                {label}
              </a>
            ))}
            <a
              href="#booking"
              onClick={go("#booking")}
              data-testid="nav-book-button"
              className="text-xs font-bold uppercase tracking-[0.2em] bg-electric hover:bg-electric-bright text-white px-5 py-2.5 transition-colors shadow-[0_0_18px_rgba(6,69,243,0.35)]"
            >
              Book a Service
            </a>
          </nav>
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-button"
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[90] bg-[#050505] flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="mobile-menu"
          >
            <div className="h-16 px-5 flex items-center justify-between border-b border-white/10">
              <LogoMark className="h-7 w-auto" />
              <button className="text-white p-2" onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu">
                <X size={22} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {[...LINKS, ["Book a Service", "#booking"]].map(([label, hash], i) => (
                <div key={hash} className="overflow-hidden">
                  <motion.a
                    href={hash}
                    onClick={go(hash)}
                    data-testid={`mobile-nav-link-${label.toLowerCase().replace(/\s/g, "-")}`}
                    className="block font-display text-4xl font-semibold uppercase tracking-tight text-white py-2 hover:text-electric-bright transition-colors"
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.5, delay: 0.08 + i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {label}
                  </motion.a>
                </div>
              ))}
            </nav>
            <p className="px-8 pb-10 text-xs uppercase tracking-[0.3em] text-gray-600">Rochdale · Greater Manchester</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
